import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Switch, Tooltip, CircularProgress, Alert, TablePagination, Tabs, Tab } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import SendIcon from '@mui/icons-material/Send';
import EmailTemplateFormModal from '../../../components/EmailTemplateFormModal';
import SendTestEmailModal from '../../../components/SendTestEmailModal';
import TemplatePreviewModal from '../../../components/TemplatePreviewModal';
import { SettingsService } from '../../../api/services/settingsService';
import { extractTotalCount } from '../../../components/common/tableStyles';

export default function MessageTemplatesTab({ setNotification, isActive }) {
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [templatesError, setTemplatesError] = useState(null);
    const [currentTab, setCurrentTab] = useState(0);

    const [templatesPage, setTemplatesPage] = useState(0);
    const [templatesRowsPerPage, setTemplatesRowsPerPage] = useState(20);
    const [totalTemplates, setTotalTemplates] = useState(0);

    const [modalOpen, setModalOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [saveLoading, setSaveLoading] = useState(false);

    const [testModalOpen, setTestModalOpen] = useState(false);
    const [templateToTest, setTemplateToTest] = useState(null);
    const [testLoading, setTestLoading] = useState(false);

    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [templateToPreview, setTemplateToPreview] = useState(null);

    useEffect(() => {
        if (isActive) {
            fetchTemplates();
        }
    }, [isActive, templatesPage, templatesRowsPerPage, currentTab]);

    const fetchTemplates = async () => {
        setTemplatesLoading(true);
        setTemplatesError(null);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const params = {
                page: templatesPage,
                limit: templatesRowsPerPage,
                category: currentTab === 0 ? 'Guest' : 'Staff'
            };
            const data = await SettingsService.getNotificationTemplates(tenantId, params);
            const templatesArray = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
            setTemplates(templatesArray);
            setTotalTemplates(extractTotalCount(data, templatesArray));
        } catch (err) {
            console.error('Failed to fetch templates:', err);
            setTemplatesError('Failed to load message templates.');
        } finally {
            setTemplatesLoading(false);
        }
    };

    const handleTemplatesChangePage = (event, newPage) => setTemplatesPage(newPage);
    const handleTemplatesChangeRowsPerPage = (event) => {
        setTemplatesRowsPerPage(parseInt(event.target.value, 10));
        setTemplatesPage(0);
    };

    const paginatedTemplates = templates;

    const handleEditTemplate = (template) => {
        setEditingTemplate(template);
        setModalOpen(true);
    };

    const handlePreviewTemplate = (template) => {
        setTemplateToPreview(template);
        setPreviewModalOpen(true);
    };

    const handleToggleTemplateStatus = async (template, currentStatus, isSystem) => {
        if (isSystem) {
            setNotification({ open: true, message: 'System templates cannot be disabled', severity: 'warning' });
            return;
        }

        const newStatus = !currentStatus;
        const previousTemplates = [...templates];
        
        setTemplates(templates.map(t => t.id === template.id ? { ...t, isActive: newStatus } : t));
        
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            await SettingsService.updateNotificationTemplate(template.id, { ...template, isActive: newStatus }, tenantId);
            setNotification({ open: true, message: 'Template status updated', severity: 'success' });
        } catch (err) {
            console.error('Failed to update template status:', err);
            setTemplates(previousTemplates);
            setNotification({ open: true, message: 'Failed to update template status', severity: 'error' });
        }
    };

    const handleSaveTemplate = async (templateData) => {
        setSaveLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';

            await SettingsService.updateNotificationTemplate(templateData.id, templateData, tenantId);
            
            await fetchTemplates();
            setModalOpen(false);
            setNotification({ open: true, message: 'Template updated successfully', severity: 'success' });
        } catch (err) {
            console.error('Failed to save template:', err);
            setNotification({ open: true, message: 'Failed to save template', severity: 'error' });
        } finally {
            setSaveLoading(false);
        }
    };

    const handleSendTest = async (email) => {
        setTestLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            await SettingsService.sendTestEmail(templateToTest.id, email, tenantId);
            setNotification({ open: true, message: 'Test email sent successfully', severity: 'success' });
            setTestModalOpen(false);
        } catch (err) {
            console.error('Failed to send test email:', err);
            setNotification({ open: true, message: 'Failed to send test email', severity: 'error' });
        } finally {
            setTestLoading(false);
        }
    };

    if (templatesLoading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                <CircularProgress />
                <Typography color="text.secondary">Fetching templates...</Typography>
            </Box>
        );
    }

    if (templatesError) {
        return <Alert severity="error">{templatesError}</Alert>;
    }



    return (
        <Box>
            <Box sx={{ borderBottom: 1, borderColor: '#eaecf0', mb: 3 }}>
                <Tabs value={currentTab} onChange={(e, newValue) => { setCurrentTab(newValue); setTemplatesPage(0); }}>
                    <Tab label="Guest Templates" />
                    <Tab label="Staff Templates" />
                </Tabs>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Template Name</TableCell>
                            <TableCell>Subject / Header</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedTemplates.length > 0 ? (
                            paginatedTemplates.map((template) => (
                                <TableRow key={template.id} hover sx={{ opacity: template.isActive === false ? 0.6 : 1 }}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">
                                            {template.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {template.slug}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {template.subject}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {template.isSystem ? (
                                            <Chip label="System" size="small" color="primary" variant="outlined" />
                                        ) : (
                                            <Chip label="Custom" size="small" color="secondary" variant="outlined" />
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title={template.isSystem ? "System templates are always active" : "Toggle active status"}>
                                            <span>
                                                <Switch 
                                                    checked={template.isActive !== false} 
                                                    onChange={() => handleToggleTemplateStatus(template, template.isActive !== false, template.isSystem)}
                                                    disabled={template.isSystem}
                                                    size="small"
                                                />
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Preview">
                                            <IconButton size="small" onClick={() => handlePreviewTemplate(template)} sx={{ color: 'text.secondary' }}>
                                                <PreviewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit">
                                            <IconButton size="small" onClick={() => handleEditTemplate(template)} sx={{ color: 'primary.main' }}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Send Test">
                                            <IconButton size="small" onClick={() => { setTemplateToTest(template); setTestModalOpen(true); }} sx={{ color: 'success.main' }}>
                                                <SendIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">No templates found</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[10, 20, 50]}
                    component="div"
                    count={templates.length === 0 ? 0 : totalTemplates}
                    rowsPerPage={templatesRowsPerPage}
                    page={templatesPage}
                    onPageChange={handleTemplatesChangePage}
                    onRowsPerPageChange={handleTemplatesChangeRowsPerPage}
                />
            </TableContainer>

            {modalOpen && (
                <EmailTemplateFormModal 
                    open={modalOpen} 
                    onClose={() => setModalOpen(false)} 
                    onSave={handleSaveTemplate} 
                    editingTemplate={editingTemplate}
                    loading={saveLoading}
                />
            )}

            <SendTestEmailModal
                open={testModalOpen}
                onClose={() => setTestModalOpen(false)}
                onSend={handleSendTest}
                templateName={templateToTest?.name}
                loading={testLoading}
            />

            <TemplatePreviewModal
                open={previewModalOpen}
                onClose={() => setPreviewModalOpen(false)}
                template={templateToPreview}
            />
        </Box>
    );
}
